import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import { Check, Vote } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { getDb } from "@/db";
import { ballots, cars, events, votes } from "@/db/schema";

// ── Server functions ────────────────────────────────────────────────

const getVotingPage = createServerFn({ method: "GET" })
	.inputValidator((data: { eventId: string; ballotId: string }) => data)
	.handler(async ({ data }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(data.eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const ballot = await db
			.select()
			.from(ballots)
			.where(
				and(
					eq(ballots.id, Number(data.ballotId)),
					eq(ballots.eventId, event.id),
				),
			)
			.get();
		if (!ballot) throw new Error("Ballot not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		return { event, ballot, cars: eventCars };
	});

const castVote = createServerFn({ method: "POST" })
	.inputValidator(
		(data: { ballotId: number; carId: number; voterToken: string }) => data,
	)
	.handler(async ({ data }) => {
		const db = getDb();

		// Check ballot is open
		const ballot = await db
			.select()
			.from(ballots)
			.where(eq(ballots.id, data.ballotId))
			.get();
		if (!ballot) throw new Error("Ballot not found");
		if (ballot.status !== "open") throw new Error("Ballot is closed");

		// Check for existing vote by this token
		const existing = await db
			.select()
			.from(votes)
			.where(
				and(
					eq(votes.ballotId, data.ballotId),
					eq(votes.voterToken, data.voterToken),
				),
			)
			.get();
		if (existing) throw new Error("Already voted");

		await db.insert(votes).values({
			ballotId: data.ballotId,
			carId: data.carId,
			voterToken: data.voterToken,
		});

		return { success: true, carId: data.carId };
	});

// ── Route ───────────────────────────────────────────────────────────

export const Route = createFileRoute("/events/$eventId/vote/$ballotId")({
	loader: ({ params }) =>
		getVotingPage({
			data: { eventId: params.eventId, ballotId: params.ballotId },
		}),
	component: PublicVotePage,
});

function getVoterToken(): string {
	const key = "pinewood-voter-token";
	let token = localStorage.getItem(key);
	if (!token) {
		token = crypto.randomUUID();
		localStorage.setItem(key, token);
	}
	return token;
}

function PublicVotePage() {
	const { event, ballot, cars: carList } = Route.useLoaderData();
	const [votedCarId, setVotedCarId] = useState<number | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [alreadyVoted, setAlreadyVoted] = useState(false);

	const sortedCars = [...carList].sort((a, b) => a.carNumber - b.carNumber);
	const divisions: string[] = (() => {
		try {
			return JSON.parse(event.divisions);
		} catch {
			return [];
		}
	})();

	// Check if this voter already voted on this ballot
	useEffect(() => {
		if (ballot.status !== "open") return;
		getVoterToken(); // ensure token exists
		const storageKey = `voted-ballot-${ballot.id}`;
		const stored = localStorage.getItem(storageKey);
		if (stored) {
			setVotedCarId(Number(stored));
			setAlreadyVoted(true);
		}
	}, [ballot.id, ballot.status]);

	const handleVote = useCallback(
		async (carId: number) => {
			if (submitting || votedCarId != null) return;
			setSubmitting(true);
			setError(null);
			try {
				const token = getVoterToken();
				await castVote({
					data: {
						ballotId: ballot.id,
						carId,
						voterToken: token,
					},
				});
				setVotedCarId(carId);
				localStorage.setItem(`voted-ballot-${ballot.id}`, String(carId));
			} catch (err) {
				const msg = err instanceof Error ? err.message : "Failed to vote";
				if (msg === "Already voted") {
					setAlreadyVoted(true);
					setVotedCarId(carId);
				} else {
					setError(msg);
				}
			} finally {
				setSubmitting(false);
			}
		},
		[ballot.id, submitting, votedCarId],
	);

	const votedCar =
		votedCarId != null ? carList.find((c) => c.id === votedCarId) : null;

	// Ballot is closed
	if (ballot.status !== "open") {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center p-6">
				<div className="bg-card rounded-xl p-8 text-center max-w-md w-full">
					<Vote size={48} className="text-muted-foreground mx-auto mb-4" />
					<h1 className="text-2xl text-foreground font-bold mb-2">
						Voting is Closed
					</h1>
					<p className="text-muted-foreground">
						This ballot for {event.name} is no longer accepting votes.
					</p>
				</div>
			</div>
		);
	}

	// Already voted
	if (votedCarId != null) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center p-6">
				<div className="bg-card rounded-xl p-8 text-center max-w-md w-full">
					<div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
						<Check size={32} className="text-success" />
					</div>
					<h1 className="text-2xl text-foreground font-bold mb-2">
						Thanks for Voting!
					</h1>
					{votedCar && (
						<div className="mt-4">
							{votedCar.avatarUrl && (
								<img
									src={votedCar.avatarUrl}
									alt=""
									className="w-24 h-24 rounded-xl object-cover mx-auto mb-3"
								/>
							)}
							<p className="text-foreground font-bold">
								<span className="text-primary text-sm mr-1">
									#{votedCar.carNumber}
								</span>
								{votedCar.carName ?? votedCar.driverName}
							</p>
						</div>
					)}
					{alreadyVoted && (
						<p className="text-muted-foreground text-sm mt-3">
							You already voted on this ballot.
						</p>
					)}
				</div>
			</div>
		);
	}

	// Voting form
	return (
		<div className="min-h-screen bg-background p-4 sm:p-6">
			<div className="max-w-3xl mx-auto">
				<div className="text-center mb-6">
					<h1 className="text-3xl text-foreground font-bold">{ballot.name}</h1>
					<p className="text-muted-foreground mt-1">{event.name}</p>
					<p className="text-muted-foreground text-sm mt-2">
						Tap a car to cast your vote
					</p>
				</div>

				{error && (
					<div className="bg-destructive/10 text-destructive rounded-lg px-4 py-3 mb-4 text-center text-sm">
						{error}
					</div>
				)}

				{divisions.length > 0 ? (
					<>
						{divisions.map((div) => {
							const divCars = sortedCars.filter((c) => c.division === div);
							if (divCars.length === 0) return null;
							return (
								<div key={div} className="mb-6">
									<h2 className="text-lg text-foreground font-bold mb-3 text-center">
										{div}
									</h2>
									<div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
										{divCars.map((car) => (
											<VoteCarCard
												key={car.id}
												car={car}
												onVote={handleVote}
												submitting={submitting}
											/>
										))}
									</div>
								</div>
							);
						})}
						{(() => {
							const noDivCars = sortedCars.filter(
								(c) => !c.division || !divisions.includes(c.division),
							);
							if (noDivCars.length === 0) return null;
							return (
								<div className="mb-6">
									<h2 className="text-lg text-foreground font-bold mb-3 text-center">
										Other
									</h2>
									<div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
										{noDivCars.map((car) => (
											<VoteCarCard
												key={car.id}
												car={car}
												onVote={handleVote}
												submitting={submitting}
											/>
										))}
									</div>
								</div>
							);
						})()}
					</>
				) : (
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
						{sortedCars.map((car) => (
							<VoteCarCard
								key={car.id}
								car={car}
								onVote={handleVote}
								submitting={submitting}
							/>
						))}
					</div>
				)}

				{submitting && (
					<div className="fixed inset-0 bg-background/50 flex items-center justify-center">
						<div className="bg-card rounded-xl p-6 text-center">
							<p className="text-foreground font-bold">Submitting vote...</p>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

function VoteCarCard({
	car,
	onVote,
	submitting,
}: {
	car: {
		id: number;
		carNumber: number;
		carName: string | null;
		driverName: string;
		avatarUrl: string | null;
		division: string | null;
	};
	onVote: (carId: number) => void;
	submitting: boolean;
}) {
	return (
		<button
			type="button"
			onClick={() => onVote(car.id)}
			disabled={submitting}
			className="bg-card rounded-xl p-4 flex flex-col items-center text-center border border-border hover:border-primary hover:bg-card/80 transition-colors disabled:opacity-50 cursor-pointer"
		>
			{car.avatarUrl ? (
				<img
					src={car.avatarUrl}
					alt=""
					className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover mb-3"
				/>
			) : (
				<div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-muted flex items-center justify-center mb-3">
					<span className="text-muted-foreground text-2xl font-bold">
						#{car.carNumber}
					</span>
				</div>
			)}
			<p className="text-foreground font-bold text-sm truncate w-full">
				{car.carName ?? car.driverName}
			</p>
			<p className="text-muted-foreground text-xs truncate w-full">
				{car.driverName}
			</p>
			{car.division && (
				<span className="mt-1 inline-flex items-center px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
					{car.division}
				</span>
			)}
		</button>
	);
}
